import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Alert, AlertContent } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { Icon } from "@/components/ui/icons";
import { LanguageSelector } from "@/components/ui/language-selector";
import { IconButton } from "@/components/ui/icon-button";
import { Text } from "@/components/ui/text";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { HStack } from "@/components/ui/h-stack";
import { Surface } from "@/components/ui/surface";
import { isValidEmail } from "@/components/ui/email-input";
import { VStack } from "@/components/ui/v-stack";
import { Heading } from "@/components/ui/heading";
import { IconCircle } from "@/components/ui/icon-circle";
import { StepCircle } from "@/components/ui/step-circle";
import {
  registerApplication,
  type RegisterApplicationRequest,
} from "@/lib/api/applications";
import { getCountries } from "@/lib/api/countries";
import { getCitiesOfCountry } from "@/lib/api/cities";
import {
  getDocumentKinds,
  filterDocumentKinds,
} from "@/lib/api/document-kinds";
import { uploadFile } from "@/lib/api/files";
import { apiClient } from "@/lib/api/client";
import type { PaginatedResponse } from "@/lib/api/types";
import { signOut, getCurrentUser } from "@/lib/api/auth";
import type { EntityApplication } from "@/lib/api/applications";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { useApplicationForm } from "@/hooks/use-application-form";
import { ApplyStepType } from "@/components/apply/apply-step-type";
import { ApplyStepDetails } from "@/components/apply/apply-step-details";
import { ApplyStepDocuments } from "@/components/apply/apply-step-documents";

// Step indicator component
const StepIndicator = ({
  currentStep,
  totalSteps,
  labels,
}: {
  currentStep: number;
  totalSteps: number;
  labels: string[];
}) => {
  return (
    <HStack justify="center" className="mb-8">
      {Array.from({ length: totalSteps }, (_, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;

        return (
          <HStack key={i}>
            <VStack align="center">
              <StepCircle
                size="md"
                variant={
                  isCompleted ? "completed" : isActive ? "active" : "inactive"
                }
                className={
                  isCompleted || isActive
                    ? "bg-[color:var(--primary)] text-[color:var(--on-primary)]"
                    : undefined
                }
              >
                {isCompleted ? <Icon.Check /> : stepNum}
              </StepCircle>
              <Text
                size="xs"
                className={`mt-1 ${
                  isActive
                    ? "text-[color:var(--primary)] font-medium"
                    : "text-[color:var(--on-surface-variant)]"
                }`}
              >
                {labels[i]}
              </Text>
            </VStack>
            {i < totalSteps - 1 && (
              <HStack
                className={`w-16 h-0.5 mx-2 ${
                  isCompleted
                    ? "bg-[color:var(--primary)]"
                    : "bg-[color:var(--surface-container)]"
                }`}
              />
            )}
          </HStack>
        );
      })}
    </HStack>
  );
};

function ApplyPage() {
  const { t: tApplication } = useTranslation("application");
  const { t: tAuth } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const navigate = useNavigate();

  const [state, dispatch] = useApplicationForm();
  const totalSteps = 3;

  // Check if user already has an active application
  const { data: existingAppsData, isLoading: checkingExistingApps } = useQuery({
    queryKey: ["entity-applications-check"],
    queryFn: () =>
      apiClient.get<PaginatedResponse<EntityApplication>>(
        "/entity-applications",
      ),
  });

  useEffect(() => {
    if (checkingExistingApps || !existingAppsData) return;
    const hasActiveApplication = existingAppsData.data.some(
      (app) => app.is_approved === false,
    );
    if (hasActiveApplication) {
      void navigate({ replace: true, to: "/application-status" });
    }
  }, [checkingExistingApps, existingAppsData, navigate]);

  // Load countries and document kinds
  const { data: catalogData } = useQuery({
    queryKey: ["apply-catalogs"],
    queryFn: async () => {
      const [countriesResult, kindsData] = await Promise.all([
        getCountries({ limit: 1000 }),
        getDocumentKinds(),
      ]);
      return { countries: countriesResult.data, documentKinds: kindsData };
    },
  });
  const countries = useMemo(() => catalogData?.countries ?? [], [catalogData]);
  const documentKinds = useMemo(
    () => catalogData?.documentKinds ?? [],
    [catalogData],
  );

  // Load cities when country changes
  const { data: cities = [], isLoading: loadingCities } = useQuery({
    queryKey: ["cities", state.countryId],
    queryFn: () => getCitiesOfCountry(state.countryId!),
    enabled: !!state.countryId,
  });

  // Filter document kinds when entity type or legal status changes
  const filteredDocumentKinds = useMemo(() => {
    if (!state.entityType) return [];
    return filterDocumentKinds(
      documentKinds,
      state.entityType,
      state.isLegalEntity,
    );
  }, [documentKinds, state.entityType, state.isLegalEntity]);

  const filteredDocumentKindsKey = useMemo(
    () => filteredDocumentKinds.map((k) => k.id).join(","),
    [filteredDocumentKinds],
  );

  // Initialize document attachments when document kinds change
  useEffect(() => {
    dispatch({
      type: "INIT_DOCUMENT_ATTACHMENTS",
      kinds: filteredDocumentKinds,
    });
  }, [filteredDocumentKinds, filteredDocumentKindsKey, dispatch]);

  const handleDocumentUpload = useCallback(
    async (kindId: string, file: File) => {
      dispatch({
        type: "UPDATE_DOCUMENT_ATTACHMENT",
        kindId,
        update: { uploading: true },
      });

      try {
        const result = await uploadFile(file);
        dispatch({
          type: "UPDATE_DOCUMENT_ATTACHMENT",
          kindId,
          update: { fileId: result.id, fileName: file.name, uploading: false },
        });
      } catch (err) {
        console.error("Upload failed:", err);
        dispatch({
          type: "UPDATE_DOCUMENT_ATTACHMENT",
          kindId,
          update: { uploading: false },
        });
      }
    },
    [dispatch],
  );

  const handleDocumentRemove = useCallback(
    (kindId: string) => {
      dispatch({ type: "REMOVE_DOCUMENT_FILE", kindId });
    },
    [dispatch],
  );

  const handleDateChange = useCallback(
    (kindId: string, field: "issueDate" | "expireDate", value: string) => {
      dispatch({
        type: "UPDATE_DOCUMENT_ATTACHMENT",
        kindId,
        update: { [field]: value },
      });
    },
    [dispatch],
  );

  const canProceedStep1 = state.entityType !== null;
  // For an individual driver (Driver + !is_legal_entity) the date is sourced
  // from the AppUser's birth_date at submit time, so no UI input is required.
  // For everything else, the user must enter the entity's incorporation date.
  const incorporationDateRequired = !(
    state.entityType === "Driver" && !state.isLegalEntity
  );
  const canProceedStep2 =
    state.companyName.trim() &&
    state.countryId &&
    state.cityId &&
    state.email.trim() &&
    isValidEmail(state.email) &&
    (!incorporationDateRequired || !!state.incorporationDate);

  const handleNext = () => {
    if (state.currentStep === 2) {
      dispatch({ type: "SET_DETAILS_ATTEMPTED" });
      if (!canProceedStep2) return;
    }
    if (state.currentStep < totalSteps)
      dispatch({ type: "SET_STEP", step: state.currentStep + 1 });
  };

  const handleBack = () => {
    if (state.currentStep > 1)
      dispatch({ type: "SET_STEP", step: state.currentStep - 1 });
  };

  const handleSubmit = async () => {
    if (!state.entityType) return;

    dispatch({ type: "SET_LOADING", loading: true });
    dispatch({ type: "SET_ERROR", error: null });

    try {
      const attachments = Object.values(state.documentAttachments)
        .filter((a) => a.fileId)
        .map((a) => ({
          kind_id: a.kindId,
          file_id: a.fileId!,
          issue_date: a.issueDate || undefined,
          expire_date: a.expireDate || undefined,
        }));

      // Source the date by entity type: individual driver pulls birth_date
      // from the signed-in AppUser (no UI input was rendered); everyone else
      // submits the date the user entered in the Details step. Refuse to
      // submit if the resolved value is missing — the canProceedStep2 gate
      // should already block this, but the runtime check guards against any
      // path that skipped validation.
      const isIndividualDriver =
        state.entityType === "Driver" && !state.isLegalEntity;
      const resolvedDate = isIndividualDriver
        ? getCurrentUser()?.birth_date
        : state.incorporationDate;
      if (!resolvedDate) {
        dispatch({
          type: "SET_ERROR",
          error: tCommon("required"),
        });
        dispatch({ type: "SET_LOADING", loading: false });
        return;
      }

      const data: RegisterApplicationRequest = {
        type: state.entityType,
        is_legal_entity: state.isLegalEntity,
        name: state.companyName,
        email: state.email,
        phone_number: state.phoneNumber,
        address: state.address,
        country_id: state.countryId,
        city_id: state.cityId,
        birth_date: resolvedDate,
        extra_info: state.extraInfo,
        act_as_shipper: state.actAsShipper,
        act_as_transport_company: state.actAsTransportCompany,
        attachments,
      };

      await registerApplication(data);
      dispatch({ type: "SET_SUCCESS" });
    } catch (err) {
      dispatch({
        type: "SET_ERROR",
        error: err instanceof Error ? err.message : tCommon("errorOccurred"),
      });
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  const stepLabels = [
    tApplication("stepType"),
    tApplication("stepDetails"),
    tApplication("stepDocuments"),
  ];

  if (state.success) {
    return (
      <VStack align="center" justify="center" className="min-h-screen p-4">
        <Card variant="elevated" padding="lg" className="max-w-md w-full">
          <VStack className="text-center">
            <IconCircle
              size="2xl"
              color="success-soft"
              className="mx-auto mb-4"
            >
              <Text as="div" color="success">
                <Icon.Check />
              </Text>
            </IconCircle>
            <Heading size="xl" className="mb-2">
              {tApplication("successTitle")}
            </Heading>
            <Text color="muted" className="mb-6">
              {tApplication("successDescription")}
            </Text>
            <Button
              type="button"
              fullWidth
              size="lg"
              onClick={() => void navigate({ to: "/application-status" })}
            >
              {tApplication("goHome")}
            </Button>
          </VStack>
        </Card>
      </VStack>
    );
  }

  if (checkingExistingApps) {
    return (
      <VStack align="center" justify="center" className="min-h-screen p-4">
        <VStack align="center" gap="lg">
          <Icon.Spinner />
          <Text color="muted">{tCommon("loading")}</Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <Surface bg="background" className="flex flex-col min-h-screen">
      {/* Header with language selector */}
      <header className="sticky top-0 z-base bg-[color:var(--surface-container)] border-b border-[color:var(--outline-variant)] shadow-sm">
        <HStack justify="between" className="container mx-auto px-4 py-3">
          <Heading as="h1" size="xl">
            {tApplication("title")}
          </Heading>
          <HStack>
            <ThemeToggle />
            <LanguageSelector />
            <IconButton
              type="button"
              onClick={async () => {
                await signOut();
                void navigate({ replace: true, to: "/login" });
              }}
              className="text-[color:var(--on-surface-variant)] hover:text-[color:var(--on-surface)]"
              title={tAuth("signOut")}
            >
              <Icon.LogOut />
            </IconButton>
          </HStack>
        </HStack>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <StepIndicator
          currentStep={state.currentStep}
          totalSteps={totalSteps}
          labels={stepLabels}
        />

        {state.error && (
          <Alert color="danger" className="mb-6">
            <AlertContent icon={<Icon.XCircle size="sm" />}>
              {state.error}
            </AlertContent>
          </Alert>
        )}

        {state.currentStep === 1 && (
          <ApplyStepType
            entityType={state.entityType}
            isLegalEntity={state.isLegalEntity}
            onEntityTypeChange={(type) =>
              dispatch({ type: "SET_ENTITY_TYPE", entityType: type })
            }
            onLegalEntityChange={(isLegal) =>
              dispatch({ type: "SET_LEGAL_ENTITY", isLegal })
            }
          />
        )}

        {state.currentStep === 2 && (
          <ApplyStepDetails
            companyName={state.companyName}
            email={state.email}
            phoneNumber={state.phoneNumber}
            address={state.address}
            countryId={state.countryId}
            cityId={state.cityId}
            incorporationDate={state.incorporationDate}
            extraInfo={state.extraInfo}
            entityType={state.entityType}
            isLegalEntity={state.isLegalEntity}
            actAsShipper={state.actAsShipper}
            actAsTransportCompany={state.actAsTransportCompany}
            detailsStepAttempted={state.detailsStepAttempted}
            countries={countries}
            cities={cities}
            loadingCities={loadingCities}
            language={language}
            dispatch={dispatch}
          />
        )}

        {state.currentStep === 3 && (
          <ApplyStepDocuments
            filteredDocumentKinds={filteredDocumentKinds}
            documentAttachments={state.documentAttachments}
            onUpload={handleDocumentUpload}
            onRemove={handleDocumentRemove}
            onDateChange={handleDateChange}
            language={language}
          />
        )}

        {/* Navigation buttons */}
        <Surface border="top" className="mt-8 pt-6">
          <HStack justify="between">
            <Button
              type="button"
              onClick={handleBack}
              disabled={state.currentStep === 1 || state.loading}
              color="neutral"
              variant="outline"
              size="lg"
            >
              <Icon.ArrowLeft />
              {tCommon("back")}
            </Button>

            {state.currentStep < totalSteps ? (
              <Button
                type="button"
                onClick={handleNext}
                disabled={
                  state.loading || (state.currentStep === 1 && !canProceedStep1)
                }
                size="lg"
              >
                {tApplication("continue")}
                <Icon.ArrowRight />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={state.loading}
                size="lg"
              >
                {state.loading ? (
                  <>
                    <Icon.Spinner />
                    {tCommon("loading")}
                  </>
                ) : (
                  tApplication("submit")
                )}
              </Button>
            )}
          </HStack>
        </Surface>
      </main>
    </Surface>
  );
}

export const Route = createFileRoute("/_app/apply")({
  component: ApplyPage,
});
